import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { ApiService } from '@api/api.service';
import { ExportServiceVerificationCode, ExportServiceVerificationCodeServiceScope } from '@api/types';
import { ExportService, ExportServiceService } from '@api/types/export_service';
import { environment } from '@env/environment';
import { RavIcon } from '@shared/icon/icon';
import { ModalService } from '@shared/modal/modal.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { PreferencesService } from '@shared/services/preferences.service';
import { ScopeService } from '@shared/services/scope.service';
import { SessionStorageService } from '@shared/services/session-storage.service';
import { AccountSettingsKeys } from '@shared/types/settings-keys';
import { get } from 'lodash';
import { Subject, Subscription } from 'rxjs';

class ExportServiceVerificationCodeBody implements ExportServiceVerificationCode {
  code: string;
  redirect_url: string;
  realmId?: string;
  service_scope: ExportServiceVerificationCodeServiceScope;

  constructor(
    code: string,
    connectionsRedirectUrl: string,
    realmId?: string,
    serviceScope?: ExportServiceVerificationCodeServiceScope
  ) {
    this.code = code;
    this.redirect_url = connectionsRedirectUrl;
    if (realmId) this.realmId = realmId;
    if (serviceScope) this.service_scope = serviceScope;
  }
}

export class Connection {
  name: string;
  active = true;
  service: ExportServiceService;
  id: string;
  requireFolders: boolean;
  folder_id?: string;
  folder_name?: string;
  key?: string;
  isConnected?: boolean;
  icon?: RavIcon;
  $serviceScope: Subject<ExportServiceVerificationCodeServiceScope> =
    new Subject<ExportServiceVerificationCodeServiceScope>();

  constructor(service?: ExportServiceService, id?: string, key?: string, folder_id?: string) {
    this.service = service;
    this.id = id;
    this.key = key;
    this.folder_id = folder_id;
    this.requireFolders = this.requireFolder(service);
    switch (service) {
      case 'dropbox':
        this.name = 'Dropbox';
        this.icon = RavIcon.DropboxLogo;
        break;
      case 'evernote':
        this.name = 'Evernote';
        this.icon = RavIcon.Evernote;
        break;
      case 'box':
        this.name = 'Box';
        this.icon = RavIcon.BoxLogo;
        break;
      case 'gdrive':
        this.name = 'Google Drive™';
        this.icon = RavIcon.GoogleDriveLogo;
        break;
      case 'sharepoint':
        this.name = 'Sharepoint';
        this.icon = RavIcon.Sharepoint;
        break;
      case 'onedrive':
        this.name = 'OneDrive';
        this.icon = RavIcon.OneDrive;
        break;
      case 'quickbooks':
        this.name = 'Quickbooks';
        this.icon = RavIcon.Quickbooks;
        break;
      case 'onenote':
        this.name = 'OneNote';
        this.icon = RavIcon.OneNote;
        break;
      case 'neat':
        this.name = 'Neat';
        this.icon = RavIcon.Neat;
        break;
      case 'clio':
        this.name = 'Clio';
        this.icon = RavIcon.Clio;
        break;
      default:
        break;
    }

    this.$serviceScope.subscribe(
      (scope: ExportServiceVerificationCodeServiceScope) => (this.requireFolders = this.requireFolder(service, scope))
    );
  }

  /**
   * A function to determine if this service require folders.(quickbooks does not require folders)
   *
   * @param service   the service name
   * @param serviceScope The scope the service has, meaning if he has full access or if it's restricted to a folder
   * @returns         false if it's quickbooks or if the scope of the service is not set to `full_access`
   */
  private requireFolder(
    service: ExportServiceService,
    serviceScope?: ExportServiceVerificationCodeServiceScope
  ): boolean {
    return !(
      service === ExportServiceService.Quickbooks ||
      serviceScope === ExportServiceVerificationCodeServiceScope.AppFolder
    );
  }
}

export interface DestinationStatus {
  active: boolean;
  destination: ExportServiceService;
}

@Component({
  selector: 'rav-connections',
  templateUrl: './connections.component.html',
  styleUrls: ['./connections.component.scss']
})
export class ConnectionsComponent implements OnInit, OnDestroy {
  userId: string;
  code: string;
  hasError = false;
  RavIcon = RavIcon;
  connectionError = '';
  connectionsRedirectUrl: string;
  routeParams: Subscription;
  currentConnection: Connection = new Connection();
  connectionWithNoFolder: Connection = new Connection();
  currentService: string;
  serviceSubject: Subject<Connection> = new Subject<Connection>();
  connections: Connection[] = [
    new Connection(ExportServiceService.Evernote),
    new Connection(ExportServiceService.Dropbox),
    new Connection(ExportServiceService.Box),
    new Connection(ExportServiceService.Gdrive),
    new Connection(ExportServiceService.Sharepoint),
    new Connection(ExportServiceService.Onedrive),
    new Connection(ExportServiceService.Quickbooks),
    new Connection(ExportServiceService.OneNote),
    new Connection(ExportServiceService.Neat),
    new Connection(ExportServiceService.Clio)
  ];

  constructor(
    private api: ApiService,
    private activatedRoute: ActivatedRoute,
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService,
    private modal: ModalService,
    private router: Router,
    public scope: ScopeService,
    private snackBar: MatSnackBar,
    private settings: PreferencesService
  ) {}

  ngOnInit() {
    this.connections.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
    this.userId = this.localStorage.userWithToken.id;
    /**
     * If there is service params in the route, it means the page comes from redirect url of the third party connection.
     * Then get the service name  and verify the code using route query params.
     */
    this.routeParams = this.activatedRoute.params.subscribe(async (params: Params) => {
      if (!params['service']) {
        await this.getActiveConnections();
      } else {
        this.currentService = params['service'];
        const { code, oauth_token, realmId } = this.activatedRoute.snapshot.queryParams;
        if (code || oauth_token) {
          // some validation codes may have been encoded by the browser the wrong way, so we replace any space
          // we may encounter with a '+' sign, which is the intended character the code should have
          const sanitizedCode = (code || oauth_token).replace(/ /gi, '+');
          await this.serviceVerifyCode(sanitizedCode, realmId, this.sessionStorage.serviceScope);
        }
        await this.getActiveConnections(true);
      }

      // Validates all connections have correct folder name
      this.connections.forEach(async (connection) => {
        if (connection.folder_id && !connection.folder_name) {
          try {
            const folder = await this.api.exportFolders.service.byId
              .get(connection.service, connection.folder_id)
              .toPromise();
            connection.folder_name = folder.name;
          } catch (error) {}
        }
      });
    });

    this.modal.close.subscribe((data) => {
      if (!data || data.modalId !== 'selectFolder') return;
      const connectionEdited = this.connections.find((connection) => connection.service === data.service);
      if (connectionEdited) {
        if (data.id) connectionEdited.folder_id = data.id;
        if (data.name) connectionEdited.folder_name = data.name;
      }
    });

    this.filterActiveDestinations();
  }

  ngOnDestroy() {
    if (this.routeParams) {
      this.routeParams.unsubscribe();
    }
  }

  /**
   * Verify the code. If succeed, connection is active and  pop up a window to select folder.
   * Otherwise pop up an error message modal.
   *
   * @param code     the verification code
   * @param realmId  the realmId of quickbooks service
   */
  async serviceVerifyCode(code: string, realmId?: string, serviceScope?: string): Promise<any> {
    this.connectionsRedirectUrl = `${environment.connectionsRedirectUrl}/${this.currentService}`;
    if (this.currentService === 'evernote') {
      this.connectionsRedirectUrl = this.activatedRoute.snapshot.queryParams['oauth_verifier'];
    }

    return this.api.users.byId.exportServices.service.verifyCode
      .patch(
        this.userId,
        this.currentService,
        new ExportServiceVerificationCodeBody(code, this.connectionsRedirectUrl, realmId, serviceScope as any)
      )
      .toPromise()
      .then((service) => {
        this.currentConnection = new Connection(service.service, service.id, service.key, service.folder_id);

        if (service.scope !== ExportServiceVerificationCodeServiceScope.AppFolder) {
          this.modal.open.next({ modalId: 'connection' });
        }
        return Promise.resolve(service);
      })
      .catch((error) => {
        this.connectionError = `Something went wrong when connecting to your ${get(
          this.currentConnection,
          'name',
          ''
        )} account.`;

        // get error from the api or show the default one
        this.connectionError = get(error, 'error.message', '') || get(error, 'message', '') || this.connectionError;
        this.hasError = true;
        this.modal.open.next({ modalId: 'connection' });
        return Promise.reject(error);
      });
  }

  /**
   * Open modal to choose folder.
   *
   * @param connection The connection from which user need to select a folder for.
   */
  async selectFolder(connection?: Connection): Promise<any> {
    this.currentConnection = connection;
    this.currentConnection.folder_id = await this.getFolderId(connection.service);
    this.modal.close.next();
    this.serviceSubject.next(this.currentConnection);
    this.modal.open.next({ modalId: 'selectFolder' });
  }

  /**
   * Disconnect the service and set the isConnected property to false. Then navigate to '/account/connections' this route.
   *
   * @param connection The connection that user select
   */
  async disconnect(connection: Connection): Promise<void> {
    if (!confirm(`Are you sure you want to disconnect ${connection.name}?`)) return;
    await this.api.exportServices.byId.delete(connection.id).toPromise();

    const deletedConnection = this.connections.find((oneConnection) => oneConnection.service === connection.service);

    if (deletedConnection) {
      deletedConnection.isConnected = false;
      deletedConnection.folder_id = '';
      deletedConnection.id = '';
    }

    if (connection.service === 'dropbox') {
      this.modal.open.next({ modalId: 'disconnect-dropbox' });
      this.modal.close.subscribe((data) => this.router.navigate(['/account/connections']));
    } else {
      this.router.navigate(['/account/connections']);
    }
  }

  /**
   * Determines which services are connected and check if a active service have folder ID if not from redirect Url.
   *
   * @param fromRedirect true if page is from third party redirect url. default is false.
   */
  getActiveConnections(fromRedirect: boolean = false): Promise<void> {
    return new Promise((resolve) => {
      this.api.users.byId.exportServices.get(this.userId).subscribe((exportServices) => {
        exportServices.forEach((service) => {
          if (service.id && !fromRedirect) this.checkConnectionWithNoFolder(service);
          const targetConnection = this.connections.find((connection) => connection.service === service.service);
          if (targetConnection) {
            targetConnection.id = service.id;
            targetConnection.isConnected = true;
            targetConnection.folder_id = service.folder_id;
            targetConnection.folder_name = service.folder_name;
            targetConnection.$serviceScope.next(<ExportServiceVerificationCodeServiceScope>service.scope);
          }
        });
        this.serviceSubject.next(this.currentConnection);
        resolve();
      });
    });
  }

  /**
   * Get an authorization url from API and redirect user to the authorization url of the third party service.
   *
   * @param connection  The connection that user try to connect to.
   */
  getAuthUrl(connection: Connection): void {
    this.currentConnection = connection;
    const servicesWithAdditionalPermissions = ['dropbox', 'gdrive'];
    this.connectionsRedirectUrl = `${environment.connectionsRedirectUrl}/${this.currentConnection.service}`;

    if (connection.service === 'sharepoint') {
      this.modal.open.next({ modalId: 'SharepointUrl' });
      return;
    }

    if (servicesWithAdditionalPermissions.includes(connection.service)) {
      this.modal.open.next({ modalId: 'addition-permissions' });
      return;
    }

    this.api.users.byId.exportServices.service.getAuthUrl
      .get(this.userId, this.currentConnection.service, this.connectionsRedirectUrl)
      .subscribe(
        (url) => {
          const link = document.createElement('a');
          link.setAttribute('href', url);
          document.body.appendChild(link);
          link.click();
          link.remove();
        },
        (error) => this.handleConnectionDestinationError(error)
      );
  }

  /**
   * Call the backend API to get user's sharepoint/dropbox authentication
   *
   * @param params Extra params to get the url
   */
  connectService(params: { [key: string]: any }) {
    this.connectionsRedirectUrl = `${environment.connectionsRedirectUrl}/${this.currentConnection.service}`;
    this.api.users.byId.exportServices.service.getAuthUrl
      .get(this.userId, this.currentConnection.service, this.connectionsRedirectUrl, params)
      .subscribe(
        (url) => {
          // save the scope in session storage to use it after the redirection
          if (params.service_scope) this.sessionStorage.serviceScope = params.service_scope;

          const link = document.createElement('a');
          link.setAttribute('href', url);
          document.body.appendChild(link);
          link.click();
          link.remove();
        },
        (error) => this.handleConnectionDestinationError(error)
      );
  }

  /**
   *  Close the modal and navigate to connections page and get the latest connection list.
   *
   */
  navigateToConnectionsPage(): void {
    this.modal.close.next();
    this.router.navigate(['/account/connections']);
  }

  /**
   * Check the connection that has no folder and prompt user to choose one.
   *
   * @param service The service object.
   */
  private checkConnectionWithNoFolder(service: ExportService) {
    if (
      this.scope.hasAccess(this.scope.scope.ExportAccountWrite) &&
      !service.folder_id &&
      service.service !== ExportServiceService.Quickbooks
    ) {
      this.connectionWithNoFolder = new Connection(service.service, service.id, service.key);
      this.modal.close.next();
      this.modal.open.next({ modalId: 'selectFolderWarning' });
    }
  }

  /**
   * Get the folder ID of the service.
   *
   * @param name The connection name
   */
  private async getFolderId(name: string): Promise<string> {
    let folderId = '';
    return this.api.users.byId.exportServices
      .get(this.userId)
      .toPromise()
      .then((exportServices) => {
        const serviceObject = <ExportService>exportServices.filter((service) => service.service === name)[0];
        folderId = serviceObject.folder_id;
        return folderId;
      });
  }

  /**
   * Updates the active property of the  collection of Connection instances  based on the account's destinations active property
   */
  private filterActiveDestinations() {
    this.settings
      .getSetting('Account', AccountSettingsKeys.destinations, [], true)
      .then((destinations: DestinationStatus[]) => {
        destinations.forEach((d) => {
          const found = this.connections.find((c) => c.service === d.destination);
          if (found) found.active = d.active;
        });
        this.connections = this.connections.filter((c) => c.active);
      });
  }

  /**
   * Handles HttpErrorResponse related to Destination's connection
   *
   * @param errorResponse Http error response
   * @returns An instance of MatSnackbar
   */
  private handleConnectionDestinationError(errorResponse: HttpErrorResponse) {
    let errorMessage = 'Unable to connect to SharePoint. Please double check your Sharepoint URL.';
    if (errorResponse.error) {
      try {
        errorMessage = JSON.parse(errorResponse.error).errors.service.join(', ');
      } catch (error) {}
    }

    return this.snackBar.open(errorMessage, '', {
      duration: 5000,
      horizontalPosition: 'center',
      panelClass: ['alert-bg', 'bold', 'light-color']
    });
  }
}
